import { PROGRAM_TYPE_GROUP_LABELS, PROGRAM_TYPE_GROUP_ORDER } from 'constants/programType';

import { PricingTargetType } from '../PricingWorkbench.constants';

/** The three independent left-panel facets. Each narrows the results; any combination is allowed. */
export const FacetKey = {
  TYPE: 'type',
  STRUCTURE: 'structure',
  TERM: 'term',
};

/**
 * The target-pricing control is a fourth left-panel option: enter a note rate OR a buy price and the
 * whole results grid re-prices to the option/row closest to it (drives the existing `target`). Unlike
 * the facets its two modes are mutually exclusive, so it's a segmented Rate/Price toggle + one input
 * rather than a chip row derived from results.
 */
export const TARGET_MODE_OPTIONS = [
  { label: 'Rate', value: PricingTargetType.RATE },
  { label: 'Price', value: PricingTargetType.PRICE },
];

/** Per-mode input placeholder — a rate reads as a note rate, a price as a buy price (rebate > 100). */
export const TARGET_PLACEHOLDER = {
  [PricingTargetType.RATE]: 'e.g. 6.500',
  [PricingTargetType.PRICE]: 'e.g. 101.000',
};

/** Unit shown inside the target input; a buy price (percent of par, e.g. 101.000) reads unitless. */
export const TARGET_UNIT = {
  [PricingTargetType.RATE]: '%',
  [PricingTargetType.PRICE]: '',
};

export const TARGET_HINT = {
  [PricingTargetType.RATE]: 'Re-prices every program at the rate closest to this.',
  [PricingTargetType.PRICE]: 'Re-prices every program at the price closest to this.',
};

/** Sentinel that clears a facet (matches everything). Shared by type/structure/term. */
export const ALL = 'All';

/** Rate-structure facet values (its own axis — a family like FHA can be Fixed OR ARM). */
export const ProgramStructure = {
  FIXED: 'Fixed',
  ARM: 'ARM',
};

export const STRUCTURE_OPTIONS = [
  { label: 'Fixed', value: ProgramStructure.FIXED },
  { label: 'ARM', value: ProgramStructure.ARM },
];

/** The leading "show everything" chip prepended to each facet row. */
export const ALL_OPTION = { label: 'All', value: ALL };

/**
 * Every program-type group, in canonical order, offered on the Type facet whether or not the scenario
 * has been priced — a chip is never withheld because the current results happen not to contain it.
 * Each chip's VALUE is a ProgramTypeGroup (Agency buckets Conventional + Conforming) and matches every
 * member type; after pricing a group with no eligible program shows a 0 count instead of vanishing.
 */
export const TYPE_OPTIONS = PROGRAM_TYPE_GROUP_ORDER.map(group => ({
  label: PROGRAM_TYPE_GROUP_LABELS[group],
  value: group,
}));

/**
 * Whole-year terms in this range are the everyday mortgage terms and sit inline on the Term facet;
 * every other term the catalog carries (month counts, 1–4 yr, hand-entered values like 11111 months)
 * stays reachable behind the facet's "more" toggle rather than being dropped.
 */
export const INLINE_TERM_MIN_YEARS = 5;
export const INLINE_TERM_MAX_YEARS = 40;

export const DEFAULT_TERM_OPTIONS = [
  { label: '30 yr', value: '30' },
  { label: '20 yr', value: '20' },
  { label: '15 yr', value: '15' },
  { label: '10 yr', value: '10' },
];
